import { useState, useEffect } from 'react';
import { profileApi, type ProfileUser } from '@/services/api';

export default function AccountSettings() {
  const [user, setUser] = useState<ProfileUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    profileApi
      .get()
      .then((data) => {
        setUser(data);
        setLoading(false);
        setForm({
          companyName: data.companyName ?? '',
          creditCode: data.creditCode ?? '',
          contactName: data.contactName,
          phone: data.phone,
          region: data.region ?? '',
          email: data.email,
        });
      })
      .catch(() => setLoading(false));
  }, []);

  const [form, setForm] = useState({
    companyName: '',
    creditCode: '',
    contactName: '',
    phone: '',
    region: '',
    email: '',
  });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await profileApi.update(form);
      setUser(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // handle error silently
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
        <p className="text-foreground-400 text-sm mt-3">加载中...</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">企业资料</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            企业名称
          </label>
          <input
            type="text"
            value={form.companyName}
            onChange={(e) => update('companyName', e.target.value)}
            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            统一社会信用代码
          </label>
          <input
            type="text"
            value={form.creditCode}
            onChange={(e) => update('creditCode', e.target.value)}
            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            联系人
          </label>
          <input
            type="text"
            value={form.contactName}
            onChange={(e) => update('contactName', e.target.value)}
            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            所在地区
          </label>
          <input
            type="text"
            value={form.region}
            onChange={(e) => update('region', e.target.value)}
            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            联系电话
          </label>
          <input
            type="text"
            value={form.phone}
            onChange={(e) => update('phone', e.target.value)}
            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            电子邮箱
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-background-200">
        <h4 className="font-heading text-base font-bold text-foreground-900 mb-4">修改密码</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              当前密码
            </label>
            <input
              type="password"
              placeholder="请输入当前密码"
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              新密码
            </label>
            <input
              type="password"
              placeholder="请输入新密码"
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              确认新密码
            </label>
            <input
              type="password"
              placeholder="请再次输入新密码"
              className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {saving ? '保存中...' : '保存修改'}
        </button>
        {saved && (
          <span className="text-primary-600 text-sm flex items-center gap-1">
            <i className="ri-check-line"></i>
            保存成功
          </span>
        )}
      </div>
    </div>
  );
}